import {
  VenueDealStatusActive,
  VenueDealStatusPaused,
  type UpsertVenueDealRequest,
  type VenueDeal,
  type VenueDealsResult,
  type VenueDealStatus,
} from '@/lib/api/apiTypes';
import { useAuth } from '@/lib/auth/auth';
import { nearbyFeedKeys } from '@/features/events/feed/feedQuery';
import { venueDetailKeys } from '@/features/venues/useVenueDetail';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

export const venueDealKeys = {
  all: ['venue-deals'] as const,
  list: (venueID: string) => [...venueDealKeys.all, venueID] as const,
};

export function useVenueDeals(venueID?: string | null, enabled = true) {
  const request = useAuth((state) => state.request);

  return useQuery({
    queryKey: venueID ? venueDealKeys.list(venueID) : venueDealKeys.all,
    enabled: Boolean(venueID) && enabled,
    queryFn: () => {
      if (!venueID) throw new Error('Missing venue id');
      return request<VenueDealsResult>(`/venues/${venueID}/deals`);
    },
    staleTime: 30_000,
  });
}

export function useCreateVenueDeal(venueID: string) {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertVenueDealRequest) =>
      request<VenueDeal>(`/venues/${venueID}/deals`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => invalidateVenueDealQueries(queryClient, venueID),
  });
}

export function useUpdateVenueDeal(venueID: string) {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealID, payload }: { dealID: string; payload: UpsertVenueDealRequest }) =>
      request<VenueDeal>(`/venues/${venueID}/deals/${dealID}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => invalidateVenueDealQueries(queryClient, venueID),
  });
}

export function useSetVenueDealStatus(venueID: string) {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealID, status }: { dealID: string; status: VenueDealStatus }) => {
      const action =
        status === VenueDealStatusPaused
          ? 'pause'
          : status === VenueDealStatusActive
            ? 'resume'
            : 'archive';
      return request<VenueDeal>(`/venues/${venueID}/deals/${dealID}/${action}`, { method: 'POST' });
    },
    onSuccess: () => invalidateVenueDealQueries(queryClient, venueID),
  });
}

export function useSkipVenueDealDate(venueID: string) {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealID, date }: { dealID: string; date: string }) =>
      request<void>(`/venues/${venueID}/deals/${dealID}/skip-date`, {
        method: 'POST',
        body: JSON.stringify({ date }),
      }),
    onSuccess: () => invalidateVenueDealQueries(queryClient, venueID),
  });
}

export function useRestoreVenueDealDate(venueID: string) {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealID, date }: { dealID: string; date: string }) =>
      request<void>(`/venues/${venueID}/deals/${dealID}/restore-date`, {
        method: 'POST',
        body: JSON.stringify({ date }),
      }),
    onSuccess: () => invalidateVenueDealQueries(queryClient, venueID),
  });
}

async function invalidateVenueDealQueries(queryClient: QueryClient, venueID: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: venueDealKeys.list(venueID) }),
    queryClient.invalidateQueries({ queryKey: venueDetailKeys.detail(venueID) }),
    queryClient.invalidateQueries({ queryKey: nearbyFeedKeys.all }),
    queryClient.invalidateQueries({ queryKey: ['nearby-venues'] }),
  ]);
}
